import React from 'react';
import { motion } from 'framer-motion';
import { Slide, FadeIn, SlideHeading, StaggerContainer, StaggerItem, Card } from './Shared';
import { ChevronDown, Target, Layers, Globe, Clock, User, Camera, Printer, Zap, Flame, Scissors, Warehouse, BoxIcon, Lightbulb } from 'lucide-react';
export const Slide1Cover = () => <Slide id="slide-1" className="items-center text-center" bg="white" data-id="element-118">
    <FadeIn delay={0.2} data-id="element-119">
      <img src="https://cdn.magicpatterns.com/uploads/aYvYevoG5MfgByUVejQyfu/LOGO_STRATA_S.png" alt="STRATA" className="w-48 md:w-60 lg:w-72 h-auto mx-auto mb-8" data-id="element-120" />
    </FadeIn>

    <FadeIn delay={0.4} data-id="element-121">
      <h1 className="text-6xl md:text-8xl lg:text-9xl font-serif font-bold text-black mb-6 tracking-tight uppercase leading-none" data-id="element-122">
        STRATA <br data-id="element-123" />
        <span className="text-4xl md:text-6xl lg:text-7xl text-slate-500" data-id="element-124">
          Creative Group
        </span>
      </h1>
    </FadeIn>

    <FadeIn delay={0.55} data-id="element-125">
      <div className="inline-block border-2 border-black text-black text-xs md:text-sm font-bold tracking-[0.3em] px-5 py-2 mb-8 uppercase" data-id="element-126">
        Creative Production House
      </div>
    </FadeIn>

    <FadeIn delay={0.7} data-id="element-127">
      <p className="text-xl md:text-3xl text-black font-serif max-w-3xl mx-auto leading-tight mb-10" data-id="element-128">
        We partner with select brands to elevate their identity through
        world-class apparel production.
      </p>
    </FadeIn>

    <FadeIn delay={0.9} data-id="element-129">
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm md:text-base text-slate-600 font-bold tracking-widest uppercase" data-id="element-130">
        <span data-id="element-131">Screen Print</span>
        <span className="text-black" data-id="element-132">•</span>
        <span data-id="element-133">DTF</span>
        <span className="text-black" data-id="element-134">•</span>
        <span data-id="element-135">DTG</span>
        <span className="text-black" data-id="element-136">•</span>
        <span data-id="element-137">Embroidery</span>
        <span className="text-black" data-id="element-138">•</span>
        <span data-id="element-139">Custom Apparel</span>
      </div>
    </FadeIn>

    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 1.5,
    duration: 1
  }} className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce" data-id="element-140">
      <ChevronDown className="w-8 h-8 text-black" data-id="element-141" />
    </motion.div>
  </Slide>;
export const Slide2ExecutiveSummary = () => <Slide id="slide-2" bg="gray" data-id="element-142">
    <SlideHeading tag="Executive Summary" title="We Are Operational" subtitle="This is not a pitch deck built on ideas. It's built on iron." data-id="element-143" />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-144">
      <FadeIn delay={0.2} data-id="element-145">
        <p className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-8 font-medium" data-id="element-146">
          We've invested heavily in building a fully operational apparel
          production facility. Equipment is installed, systems are live, and
          we've completed our first B2B orders.
        </p>
        <p className="text-lg md:text-xl text-slate-600 leading-relaxed" data-id="element-147">
          Now we're raising a strategic round from our network to fuel customer
          acquisition and scale operations without putting unnecessary pressure
          on the partnership during the startup phase.
        </p>
      </FadeIn>

      <StaggerContainer className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-148">
        {[{
        value: '1B+',
        label: 'Garments Produced',
        icon: Target
      }, {
        value: '$600K+',
        label: 'Ready-to-Print IP',
        icon: Layers
      }, {
        value: 'LIVE',
        label: 'Doors Are Open',
        icon: Clock
      }, {
        value: '3',
        label: 'Country Team',
        icon: Globe
      }].map((kpi, i) => <StaggerItem key={i} data-id="element-149">
            <Card dark className="text-center py-10 flex flex-col items-center justify-center h-full" data-id="element-150">
              <kpi.icon className="w-8 h-8 text-white mx-auto mb-4" data-id="element-151" />
              <div className="text-5xl md:text-6xl font-serif font-bold text-white mb-2" data-id="element-152">
                {kpi.value}
              </div>
              <div className="text-sm md:text-base text-slate-300 font-bold uppercase tracking-widest" data-id="element-153">
                {kpi.label}
              </div>
            </Card>
          </StaggerItem>)}
      </StaggerContainer>
    </div>
  </Slide>;
export const Slide3Founders = () => <Slide id="slide-3" bg="white" data-id="element-154">
    <SlideHeading tag="The Team" title="Meet the Founders" subtitle="We're raising from people who know us — our track record speaks for itself." data-id="element-155" />

    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-8" data-id="element-156">
      <FadeIn delay={0.2} data-id="element-157">
        <Card className="h-full flex flex-col" data-id="element-158">
          <div className="w-full h-[28rem] border-2 border-black mb-6 overflow-hidden bg-black" data-id="element-159">
            <img src="https://cdn.magicpatterns.com/uploads/iH8vEpR1PNqZ9Foi3svT3y/FRANCISCO_KELLY.jpg" alt="Francisco Kelly" className="w-full h-full object-cover object-[center_30%]" data-id="element-160" />
          </div>
          <h3 className="text-3xl font-serif font-bold text-black uppercase mb-2" data-id="element-161">
            Francisco Kelly
          </h3>
          <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4" data-id="element-162">
            CEO — Operations, Infrastructure & Expansion
          </p>
          <p className="text-slate-600 text-lg leading-relaxed" data-id="element-163">
            A multidisciplinary operator with experience across textile,
            entertainment, and restaurant businesses, and owner of a
            construction company specializing in commercial build-outs. Brings
            operational structure, logistical expertise, and expansion
            capabilities to STRATA's physical and commercial footprint.
          </p>
        </Card>
      </FadeIn>

      <FadeIn delay={0.4} data-id="element-164">
        <Card className="h-full flex flex-col" data-id="element-165">
          <div className="w-full h-[28rem] border-2 border-black mb-6 overflow-hidden bg-black" data-id="element-166">
            <img src="https://cdn.magicpatterns.com/uploads/83mvhjpptRvavtj5uBs1iM/SANTIAGO_COHEN.png" alt="Santiago Cohen" className="w-full h-full object-cover object-top" data-id="element-167" />
          </div>
          <h3 className="text-3xl font-serif font-bold text-black uppercase mb-2" data-id="element-168">
            Santiago Cohen
          </h3>
          <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4" data-id="element-169">
            COO — Head of Creative Manufacturing
          </p>
          <p className="text-slate-600 text-lg leading-relaxed" data-id="element-170">
            <strong className="text-black font-bold" data-id="element-171">30+ years</strong> in
            screen printing, color separation, apparel design, and high-volume
            production across the U.S. and Mexico. Has overseen production of{' '}
            <strong className="text-black font-bold" data-id="element-172">
              1 billion+ printed garments
            </strong>{' '}
            and created{' '}
            <strong className="text-black font-bold" data-id="element-173">
              3,000+ original designs
            </strong>{' '}
            for brands including Walmart, Target, Warner Bros., Nike, Gap, Old
            Navy, Marvel, and Disney Consumer Products. A specialist in workflow
            engineering, automation, and scalable production systems.
          </p>
        </Card>
      </FadeIn>
    </div>

    <FadeIn delay={0.6} data-id="element-174">
      <div className="border-t-2 border-black pt-10" data-id="element-175">
        <div className="flex items-center gap-4 mb-8" data-id="element-176">
          <span className="text-sm font-bold uppercase tracking-widest text-slate-500" data-id="element-177">
            Santiago's Production Experience Includes Work For
          </span>
          <div className="h-px bg-slate-300 flex-1" data-id="element-178" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-8 md:gap-10 items-center" data-id="element-179">
          {[{
          name: 'Nike',
          logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a6/Logo_NIKE.svg'
        }, {
          name: 'Disney',
          logo: 'https://cdn.magicpatterns.com/uploads/vQ7YYAQZ7DmpPfNdG2TByD/pasted-image.jpg'
        }, {
          name: 'Walmart',
          logo: 'https://cdn.magicpatterns.com/uploads/3RAwrjVvpAhcfJrfsLch1z/pasted-image.webp'
        }, {
          name: 'Target',
          logo: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Target_logo.svg'
        }, {
          name: 'Warner Bros.',
          logo: 'https://cdn.magicpatterns.com/uploads/dc76PgL5dSCSkMWsfrDVAY/image.png'
        }, {
          name: 'Gap',
          logo: 'https://cdn.magicpatterns.com/uploads/s6yQBfQWJ33P2fVqpawmQm/image.png'
        }, {
          name: 'Old Navy',
          logo: 'https://cdn.magicpatterns.com/uploads/j7umHuLo9VNp39wK6PG6fD/image.png'
        }, {
          name: 'Marvel',
          logo: 'https://cdn.magicpatterns.com/uploads/fKZZ7rr4B8L8au5WmnpBnC/image.png'
        }].map((brand, i) => <div key={i} className="flex items-center justify-center h-16" data-id="element-180">
              {brand.logo ? <img src={brand.logo} alt={brand.name} className="max-h-12 max-w-full object-contain opacity-100 hover:grayscale hover:opacity-80 transition-all duration-300" data-id="element-181" /> : <span className="text-center text-base md:text-lg font-serif font-bold text-black uppercase tracking-tight" data-id="element-182">
                  {brand.name}
                </span>}
            </div>)}
        </div>
      </div>
    </FadeIn>
  </Slide>;
export const Slide4WhatsBuilt = () => <Slide id="slide-4" bg="gray" data-id="element-183">
    <SlideHeading tag="Production Equipment" title="Our Production Arsenal" subtitle="Every machine purchased. Every system installed. Every piece operational and ready to produce." data-id="element-184" />

    <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-id="element-185">
      {[{
      title: 'STS DTF Printer',
      desc: 'Top of the line direct-to-film',
      icon: Printer
    }, {
      title: 'STS Powder Shaker',
      desc: 'Conveyor dryer and cure unit',
      icon: Flame
    }, {
      title: 'Epson DTG Printer',
      desc: 'Direct-to-garment production',
      icon: Printer
    }, {
      title: 'Pre-Treatment Station',
      desc: 'Garment prep system',
      icon: BoxIcon
    }, {
      title: 'Laser Cutters/Engravers',
      desc: 'Leather patches, wood, acrylic',
      icon: Scissors
    }, {
      title: 'Epson Film Positives Printer',
      desc: 'Large format specialty printer',
      icon: Printer
    }, {
      title: 'Screen Exposure Unit',
      desc: 'Precision screen burning for print production',
      icon: Lightbulb
    }, {
      title: 'Hopkins Manual Press',
      desc: 'Top tier 8 color 6 station screen printing',
      icon: Zap
    }, {
      title: '2-Head Embroidery',
      desc: 'Premium custom stitching',
      icon: Layers
    }, {
      title: 'Conveyor Oven/Dryer',
      desc: 'Industrial curing',
      icon: Flame
    }, {
      title: 'Heat Presses',
      desc: 'Multiple transfer stations',
      icon: BoxIcon
    }, {
      title: 'Full Warehouse',
      desc: 'Rented and operational',
      icon: Warehouse
    }].map((item, i) => <StaggerItem key={i} data-id="element-186">
          <Card className="flex items-start gap-4 p-6 h-full" data-id="element-187">
            <div className="w-12 h-12 bg-black flex items-center justify-center shrink-0" data-id="element-188">
              <item.icon className="w-6 h-6 text-white" data-id="element-189" />
            </div>
            <div data-id="element-190">
              <h4 className="text-xl font-serif font-bold text-black uppercase mb-1" data-id="element-191">
                {item.title}
              </h4>
              <p className="text-slate-600 text-sm font-medium" data-id="element-192">{item.desc}</p>
            </div>
          </Card>
        </StaggerItem>)}
    </StaggerContainer>
  </Slide>;
export const Slide5DesignArsenal = () => <Slide id="slide-5" bg="white" data-id="element-193">
    <SlideHeading tag="Intellectual Property" title="The Design Arsenal" subtitle="This library would cost a new company years and hundreds of thousands to build. We already have it." data-id="element-194" />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8 items-center" data-id="element-195">
      <FadeIn delay={0.2} data-id="element-196">
        <div className="border-4 border-black p-10 md:p-16 bg-black text-white text-center" data-id="element-197">
          <div className="text-7xl md:text-8xl font-serif font-bold leading-none mb-4" data-id="element-198">
            1,000+
          </div>
          <div className="text-2xl font-bold uppercase tracking-widest text-slate-300 mb-8" data-id="element-199">
            Premium Designs
          </div>
          <div className="h-1 w-16 bg-white mx-auto mb-8" data-id="element-200" />
          <div className="text-4xl md:text-5xl font-serif font-bold text-white mb-2" data-id="element-201">
            $600K - $1.2M
          </div>
          <div className="text-sm font-bold uppercase tracking-widest text-slate-400" data-id="element-202">
            In Ready-to-Monetize IP Value
          </div>
        </div>
      </FadeIn>

      <StaggerContainer className="space-y-6" data-id="element-203">
        <StaggerItem data-id="element-204">
          <Card className="p-8" data-id="element-205">
            <h4 className="text-2xl font-serif font-bold text-black uppercase mb-3" data-id="element-206">
              Built Over 5 Years
            </h4>
            <p className="text-slate-600 text-lg" data-id="element-207">
              Santiago's personal art collection, curated over years with the
              vision of creating and launching his own brand — what today is "Ke
              Chingón." These are proven, high-quality assets ready to monetize.
            </p>
          </Card>
        </StaggerItem>
        <StaggerItem data-id="element-208">
          <Card className="p-8" data-id="element-209">
            <h4 className="text-2xl font-serif font-bold text-black uppercase mb-3" data-id="element-210">
              Market Proven Value
            </h4>
            <p className="text-slate-600 text-lg" data-id="element-211">
              Each design historically sold for{' '}
              <strong className="text-black font-bold" data-id="element-212">$600-$1,200</strong> at
              market rates. Producing 30 new designs a month today would cost a
              fortune.
            </p>
          </Card>
        </StaggerItem>
        <StaggerItem data-id="element-213">
          <Card className="p-8" data-id="element-214">
            <h4 className="text-2xl font-serif font-bold text-black uppercase mb-3" data-id="element-215">
              Immediate Monetization
            </h4>
            <p className="text-slate-600 text-lg" data-id="element-216">
              Ready to print on demand, launch as seasonal collections, or
              license to other brands immediately.
            </p>
          </Card>
        </StaggerItem>
      </StaggerContainer>

      <FadeIn delay={0.4} className="mt-16" data-id="element-217">
        <div className="flex items-center gap-4 mb-8" data-id="element-218">
          <div className="h-px bg-slate-300 flex-1" data-id="element-219" />
          <span className="text-sm font-bold uppercase tracking-widest text-slate-500" data-id="element-220">
            Art Arsenal Samples
          </span>
          <div className="h-px bg-slate-300 flex-1" data-id="element-221" />
        </div>

        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto" data-id="element-222">
          {[{
          url: 'https://cdn.magicpatterns.com/uploads/iCW3Ff7ug61246qm4xrV4c/pasted-image.png',
          alt: 'Golden ornate jaguar'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/rufBaBetS8ui92oCXRGtVe/pasted-image.png',
          alt: 'Aztec warrior with jaguar headdress'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/uyJoCrFhXokjNw4cr8iEg3/pasted-image.png',
          alt: 'Decorated sugar skull'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/8aZAiiTyue4h43k68hWWRH/pasted-image.png',
          alt: 'Black panther with golden eyes'
        }].map((img, i) => <StaggerItem key={i} data-id="element-223">
              <div className="group relative aspect-[4/5] overflow-hidden border-4 border-black bg-black" data-id="element-224">
                <img src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100" data-id="element-225" />
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/20 transition-colors duration-500 pointer-events-none" data-id="element-226" />
              </div>
            </StaggerItem>)}
        </StaggerContainer>
      </FadeIn>
    </div>
  </Slide>;